import type { Plugin } from 'unified'
import type { PhrasingContent, Root } from 'mdast'
import { findAndReplace } from 'mdast-util-find-and-replace'

/** Matches inline citation markers the model appends after facts about the customer's own data, e.g. `[e3]`. */
const CITE_MARKER = /\[e\d+\]/g

/**
 * remark plugin (Epic A #7 Tier 2): turn `[eN]` markers in the assistant answer into inline
 * citation chips. Runs on the mdast tree AFTER remark-gfm, so it only ever touches `Text` nodes — markers
 * inside code spans (which are `InlineCode.value`, not `Text`) are naturally left alone.
 *
 * Resolution is done HERE against the turn's real evidence ids (`validIds`), which gives the anti-hallucination
 * guarantee deterministically:
 *   - resolvable id  → a custom node compiled to a `<cite>` hast element carrying `evidenceId`, which
 *                       react-markdown maps to the chip component via its `components` prop;
 *   - invented id    → replaced with the empty string, i.e. the token is STRIPPED, so no broken chip ever
 *                       reaches the DOM.
 *
 * `<cite>` is used purely as a transport tag: markdown has no native `<cite>` production and react-markdown
 * does not parse raw HTML, so every `<cite>` element in the answer originates from this plugin.
 */
export function remarkCiteRefs(validIds: Set<string>): Plugin<[], Root> {
  return () => (tree: Root): undefined => {
    findAndReplace(tree, [
      CITE_MARKER,
      (marker: string): PhrasingContent | string => {
        const id = marker.slice(1, -1) // "[e3]" → "e3"
        if (!validIds.has(id)) return '' // invented id → strip the token entirely
        // Custom mdast node: no known handler, so mdast-util-to-hast's unknown-handler honors `data.hName`
        // and emits `<cite evidenceId="eN">`. Cast because this shape is intentionally outside the mdast union.
        const node = { type: 'citeRef', data: { hName: 'cite', hProperties: { evidenceId: id } } }
        return node as unknown as PhrasingContent
      }
    ])
    return undefined
  }
}
